import React from "react";
import { Area, Line } from "recharts";
import { Brand100, Brand25, Brand50, Brand70, Neutral400, Neutral500 } from "../../utils/colors";
import {
  ForecastKpiViewOption,
  HISTORICAL_KEY,
  KPI_CI_VIEW_SUFFICES,
  KPI_VIEW_SUFFICES
} from "../forecastingConstants";

export const buildKpiPrimaryOnlyGraphs = (
  primaryBudget: number,
  kpiView: ForecastKpiViewOption
): React.ReactNode => (
  <>
    <Line
      dataKey={`${HISTORICAL_KEY}-${KPI_VIEW_SUFFICES[kpiView]}`}
      dot={false}
      isAnimationActive={false}
      stroke={Brand25}
      strokeWidth={3}
      type="monotone"
    />
    <Area
      dataKey={`${primaryBudget}-${KPI_CI_VIEW_SUFFICES[kpiView]}`}
      fill={Brand25}
      isAnimationActive={false}
      stroke="transparent"
      fillOpacity={1}
      activeDot={false}
    />
    <Line
      dataKey={`${primaryBudget}-${KPI_VIEW_SUFFICES[kpiView]}`}
      dot={false}
      isAnimationActive={false}
      stroke={Brand70}
      strokeDasharray="4"
      strokeWidth={3}
      type="monotone"
    />
  </>
);

export const buildKpiComparisonGraphs = (
  visibleBudgets: number[],
  hoveredBudget: number | undefined,
  selectedBudget: number | undefined,
  colorMap: Record<string, string>,
  kpiView: ForecastKpiViewOption
): React.ReactNode => (
  <>
    <Area
      dataKey={`${HISTORICAL_KEY}-${KPI_VIEW_SUFFICES[kpiView]}`}
      fill={Brand25}
      isAnimationActive={false}
      stroke="transparent"
      fillOpacity={1}
      activeDot={false}
    />
    {visibleBudgets.map(budget => {
      const isHovered = hoveredBudget && hoveredBudget === budget;
      const isShadowed = hoveredBudget && hoveredBudget !== budget;
      const isSelected = selectedBudget && selectedBudget === budget;
      const showConfidenceInterval = isHovered || isSelected;
      const lineColor = isShadowed ? Neutral400 : colorMap[budget];

      return (
        <React.Fragment key={`${budget}-kpi-graph`}>
          {showConfidenceInterval && (
            <Area
              dataKey={`${hoveredBudget ?? selectedBudget}-${KPI_CI_VIEW_SUFFICES[kpiView]}`}
              fill={colorMap[budget]}
              isAnimationActive={false}
              stroke="transparent"
              fillOpacity={0.25}
              activeDot={false}
            />
          )}
          <Line
            dataKey={`${budget}-${KPI_VIEW_SUFFICES[kpiView]}`}
            dot={false}
            isAnimationActive={false}
            stroke={lineColor}
            strokeDasharray="4"
            strokeWidth={3}
            type="monotone"
          />
        </React.Fragment>
      );
    })}
  </>
);

export const buildSpendPrimaryOnlyGraphs = (primaryBudget: number): React.ReactNode => (
  <>
    <Area
      dataKey={`${HISTORICAL_KEY}-spend`}
      yAxisId={0}
      fill={Brand25}
      isAnimationActive={false}
      stroke="transparent"
      fillOpacity={1}
      type="monotone"
      activeDot={false}
    />
    <Area
      dataKey={`${primaryBudget}-spend`}
      yAxisId={0}
      fill={`url(#shadedStripeNeg${Brand50})`}
      isAnimationActive={false}
      stroke="transparent"
      fillOpacity={1}
      type="monotone"
      activeDot={false}
    />
    <Line
      dataKey={`${HISTORICAL_KEY}-cpm`}
      yAxisId={1}
      dot={false}
      isAnimationActive={false}
      stroke={Neutral500}
      strokeWidth={3}
      type="monotone"
    />
    <Line
      dataKey={`${primaryBudget}-cpm`}
      yAxisId={1}
      dot={false}
      isAnimationActive={false}
      stroke={Brand100}
      strokeDasharray="4"
      strokeWidth={3}
      type="monotone"
    />
  </>
);

export const buildSpendComparisonGraphs = (
  visibleBudgets: number[],
  hoveredBudget: number | undefined,
  colorMap: Record<string, string>
): React.ReactNode => (
  <>
    <Area
      dataKey={`${HISTORICAL_KEY}-spend`}
      yAxisId={0}
      fill={Brand25}
      isAnimationActive={false}
      stroke={Brand25}
      fillOpacity={1}
      activeDot={false}
    />
    {visibleBudgets.map(budget => {
      const isShadowed = hoveredBudget && hoveredBudget !== budget;
      const lineColor = isShadowed ? Neutral400 : colorMap[budget];

      return (
        <Line
          key={`${budget}-spend-line`}
          dataKey={`${budget}-spend`}
          yAxisId={0}
          dot={false}
          isAnimationActive={false}
          stroke={lineColor}
          strokeDasharray="4"
          strokeWidth={3}
          type="monotone"
        />
      );
    })}
  </>
);
